import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

// 디자인 시스템
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    success: '#00B894',
    warning: '#FDCB6E',
    danger: '#E17055',
    bgPrimary: '#FAFBFC',
    bgSecondary: '#FFFFFF',
    textPrimary: '#1A202C',
    textSecondary: '#718096',
    textMuted: '#A0AEC0',
    border: '#E2E8F0'
  }
};

interface SitePage {
  id: number;
  site_name: string;
  main_page_url: string | null;
  category_page_url: string | null;
  product_detail_url: string | null;
  login_signup_url: string | null;
  cart_url: string | null;
  other_page_1_url: string | null;
  other_page_2_url: string | null;
  other_page_3_url: string | null;
  other_page_4_url: string | null;
  other_page_5_url: string | null;
  created_at: string;
  updated_at: string;
}

const SitePagesManager: React.FC = () => {
  const { isAdmin, user, isLoading } = useAuth();
  const [sites, setSites] = useState<SitePage[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // 편집 상태
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<SitePage>>({});

  // 선택 상태 (대량 삭제용)
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // 기타 페이지 표시 여부
  const [showOtherPages, setShowOtherPages] = useState(false);

  // CSV 업로드 상태
  const [uploadStatus, setUploadStatus] = useState('');

  // 새 사이트 추가 상태
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<Partial<SitePage>>({
    site_name: '',
    main_page_url: '',
    category_page_url: '',
    product_detail_url: '',
    login_signup_url: '',
    cart_url: '',
    other_page_1_url: '',
    other_page_2_url: '',
    other_page_3_url: '',
    other_page_4_url: '',
    other_page_5_url: ''
  });

  useEffect(() => {
    if (!isLoading && isAdmin && user) {
      fetchSites();
    }
  }, [isAdmin, user, isLoading, searchQuery]);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(
        getApiUrl(`/api/admin/site-pages?search=${encodeURIComponent(searchQuery)}&limit=100`),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('사이트 목록 조회 실패');
      }

      const result = await response.json();
      setSites(result.data);
    } catch (error) {
      console.error('❌ 사이트 목록 조회 실패:', error);
      alert('사이트 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 편집 시작
  const startEdit = (site: SitePage) => {
    setEditingId(site.id);
    setEditForm({ ...site });
  };

  // 편집 취소
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // 편집 저장
  const saveEdit = async () => {
    if (!editingId) return;

    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(
        getApiUrl(`/api/admin/site-pages/${editingId}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editForm)
        }
      );

      if (!response.ok) {
        throw new Error('수정 실패');
      }

      alert('수정되었습니다.');
      cancelEdit();
      fetchSites();
    } catch (error) {
      console.error('❌ 수정 실패:', error);
      alert('수정에 실패했습니다.');
    }
  };

  // 새 사이트 추가
  const handleAdd = async () => {
    if (!addForm.site_name || !addForm.site_name.trim()) {
      alert('사이트명은 필수입니다.');
      return;
    }

    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(
        getApiUrl('/api/admin/site-pages'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(addForm)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '추가 실패');
      }

      alert('추가되었습니다.');
      setShowAddForm(false);
      setAddForm({
        site_name: '',
        main_page_url: '',
        category_page_url: '',
        product_detail_url: '',
        login_signup_url: '',
        cart_url: '',
        other_page_1_url: '',
        other_page_2_url: '',
        other_page_3_url: '',
        other_page_4_url: '',
        other_page_5_url: ''
      });
      fetchSites();
    } catch (error: any) {
      console.error('❌ 추가 실패:', error);
      alert(error.message || '추가에 실패했습니다.');
    }
  };

  // 추가 폼 취소
  const cancelAdd = () => {
    setShowAddForm(false);
    setAddForm({
      site_name: '',
      main_page_url: '',
      category_page_url: '',
      product_detail_url: '',
      login_signup_url: '',
      cart_url: '',
      other_page_1_url: '',
      other_page_2_url: '',
      other_page_3_url: '',
      other_page_4_url: '',
      other_page_5_url: ''
    });
  };

  // 삭제
  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(
        getApiUrl(`/api/admin/site-pages/${id}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('삭제 실패');
      }

      alert('삭제되었습니다.');
      fetchSites();
    } catch (error) {
      console.error('❌ 삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  // 대량 삭제
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert('삭제할 사이트를 선택해주세요.');
      return;
    }

    if (!window.confirm(`${selectedIds.length}개 사이트를 삭제하시겠습니까?`)) return;

    try {
      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(
        getApiUrl('/api/admin/site-pages/bulk-delete'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ ids: selectedIds })
        }
      );

      if (!response.ok) {
        throw new Error('대량 삭제 실패');
      }

      alert(`${selectedIds.length}개 사이트가 삭제되었습니다.`);
      setSelectedIds([]);
      fetchSites();
    } catch (error) {
      console.error('❌ 대량 삭제 실패:', error);
      alert('대량 삭제에 실패했습니다.');
    }
  };

  // 체크박스 토글
  const toggleSelection = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // 전체 선택/해제
  const toggleSelectAll = () => {
    if (selectedIds.length === sites.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(sites.map(s => s.id));
    }
  };

  // CSV 템플릿 다운로드 (샘플)
  const downloadTemplate = () => {
    const csv = '사이트명,메인페이지,상품카테고리,상품상세,로그인/회원가입,장바구니,기타페이지1,기타페이지2,기타페이지3,기타페이지4,기타페이지5\n' +
      '쿠팡,https://www.coupang.com,https://www.coupang.com/np/categories/194176,https://www.coupang.com/vp/products/123456,https://login.coupang.com,https://www.coupang.com/cart,,,,,\n';

    const BOM = '\uFEFF'; // UTF-8 BOM for Excel
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'site_pages_template.csv';
    link.click();
  };

  // 전체 데이터 CSV 다운로드
  const downloadAllData = () => {
    if (sites.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    // 헤더
    let csv = '사이트명,메인페이지,상품카테고리,상품상세,로그인/회원가입,장바구니,기타페이지1,기타페이지2,기타페이지3,기타페이지4,기타페이지5\n';

    // 데이터 행
    sites.forEach(site => {
      const row = [
        site.site_name,
        site.main_page_url || '',
        site.category_page_url || '',
        site.product_detail_url || '',
        site.login_signup_url || '',
        site.cart_url || '',
        site.other_page_1_url || '',
        site.other_page_2_url || '',
        site.other_page_3_url || '',
        site.other_page_4_url || '',
        site.other_page_5_url || ''
      ].map(field => {
        // CSV 이스케이프 처리 (쉼표나 따옴표가 포함된 경우)
        if (field.includes(',') || field.includes('"') || field.includes('\n')) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      }).join(',');

      csv += row + '\n';
    });

    const BOM = '\uFEFF'; // UTF-8 BOM for Excel
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    // 현재 날짜를 파일명에 포함
    const today = new Date().toISOString().split('T')[0];
    link.download = `site_pages_${today}.csv`;
    link.click();
  };

  // CSV 업로드
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadStatus('업로드 중...');

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      // 헤더 제외
      const sites = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        return {
          site_name: values[0] || '',
          main_page_url: values[1] || '',
          category_page_url: values[2] || '',
          product_detail_url: values[3] || '',
          login_signup_url: values[4] || '',
          cart_url: values[5] || '',
          other_page_1_url: values[6] || '',
          other_page_2_url: values[7] || '',
          other_page_3_url: values[8] || '',
          other_page_4_url: values[9] || '',
          other_page_5_url: values[10] || ''
        };
      }).filter(s => s.site_name);

      const token = localStorage.getItem('google_auth_token');
      const response = await fetch(
        getApiUrl('/api/admin/site-pages/bulk'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ sites })
        }
      );

      if (!response.ok) {
        throw new Error('CSV 업로드 실패');
      }

      const result = await response.json();
      setUploadStatus(`완료: 생성 ${result.data.created}개, 수정 ${result.data.updated}개`);
      fetchSites();

      // 입력 리셋
      event.target.value = '';
    } catch (error) {
      console.error('❌ CSV 업로드 실패:', error);
      setUploadStatus('업로드 실패');
      alert('CSV 업로드에 실패했습니다.');
    }
  };

  // URL 링크 렌더링 (편집 모드가 아닐 때)
  const renderUrlLink = (url: string | null) => {
    if (!url) return <span style={{ color: designSystem.colors.textMuted }}>-</span>;

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: designSystem.colors.primary,
          textDecoration: 'none',
          fontSize: '0.875rem',
          display: 'block',
          maxWidth: '200px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
      >
        {url}
      </a>
    );
  };

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>로딩 중...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: designSystem.colors.danger }}>관리자 권한이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', background: designSystem.colors.bgPrimary, minHeight: '100vh' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: designSystem.colors.textPrimary,
          marginBottom: '0.5rem'
        }}>
          사이트 페이지 관리
        </h2>
      </div>

      {/* CSV 가이드 */}
      <div style={{
        background: designSystem.colors.primaryLight,
        border: `1px solid ${designSystem.colors.primary}`,
        borderRadius: '0.5rem',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          color: designSystem.colors.textPrimary,
          marginBottom: '0.5rem'
        }}>
          CSV 업로드 가이드
        </h3>
        <ul style={{
          fontSize: '0.75rem',
          color: designSystem.colors.textSecondary,
          margin: 0,
          paddingLeft: '1.5rem',
          lineHeight: '1.6'
        }}>
          <li>컬럼 순서: 사이트명, 메인페이지, 상품카테고리, 상품상세, 로그인/회원가입, 장바구니, 기타페이지1~5</li>
          <li>사이트명이 이미 존재하면 URL 정보를 업데이트합니다.</li>
          <li>사이트명은 필수이며, 나머지 URL은 선택 입력입니다.</li>
          <li>템플릿을 다운로드하여 양식을 확인하세요.</li>
        </ul>
      </div>

      {/* 컨트롤 영역 */}
      <div style={{
        display: 'flex',
        gap: '1.5rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* 그룹 1: 데이터 관리 */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          paddingRight: '1.5rem',
          borderRight: `2px solid ${designSystem.colors.border}`
        }}>
          {/* 검색 */}
          <input
            type="text"
            placeholder="사이트명 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '0.5rem 0.75rem',
              border: `1px solid ${designSystem.colors.border}`,
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              width: '200px'
            }}
          />

          {/* 새 사이트 추가 버튼 */}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '0.5rem 1rem',
              background: showAddForm ? designSystem.colors.textMuted : designSystem.colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {showAddForm ? '취소' : '+ 새 사이트 추가'}
          </button>

          {/* 대량 삭제 */}
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              style={{
                padding: '0.5rem 1rem',
                background: designSystem.colors.danger,
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              선택 삭제 ({selectedIds.length})
            </button>
          )}
        </div>

        {/* 그룹 2: CSV 작업 */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          paddingRight: '1.5rem',
          borderRight: `2px solid ${designSystem.colors.border}`
        }}>
          {/* 업로드 샘플 다운로드 */}
          <button
            onClick={downloadTemplate}
            style={{
              padding: '0.5rem 1rem',
              background: designSystem.colors.textMuted,
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            업로드 샘플
          </button>

          {/* CSV 업로드 */}
          <label style={{
            padding: '0.5rem 1rem',
            background: designSystem.colors.primary,
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer'
          }}>
            CSV 업로드
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>

          {/* CSV 다운로드 (전체 데이터) */}
          <button
            onClick={downloadAllData}
            style={{
              padding: '0.5rem 1rem',
              background: designSystem.colors.success,
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            CSV 다운로드
          </button>

          {uploadStatus && (
            <span style={{ fontSize: '0.875rem', color: designSystem.colors.textSecondary }}>
              {uploadStatus}
            </span>
          )}
        </div>

        {/* 그룹 3: 표시 옵션 */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          marginLeft: 'auto'
        }}>
          {/* 기타 페이지 표시 토글 */}
          <button
            onClick={() => setShowOtherPages(!showOtherPages)}
            style={{
              padding: '0.5rem 1rem',
              background: showOtherPages ? designSystem.colors.warning : designSystem.colors.textMuted,
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            {showOtherPages ? '기타 페이지 숨김' : '기타 페이지 표시'}
          </button>
        </div>
      </div>

      {/* 새 사이트 추가 폼 */}
      {showAddForm && (
        <div style={{
          background: designSystem.colors.primaryLight,
          border: `2px solid ${designSystem.colors.primary}`,
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: designSystem.colors.textPrimary,
            marginBottom: '1rem'
          }}>
            새 사이트 추가
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: designSystem.colors.textPrimary,
                marginBottom: '0.25rem'
              }}>
                사이트명 <span style={{ color: designSystem.colors.danger }}>*</span>
              </label>
              <input
                type="text"
                value={addForm.site_name}
                onChange={(e) => setAddForm({ ...addForm, site_name: e.target.value })}
                placeholder="예: 쿠팡"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: designSystem.colors.textPrimary,
                marginBottom: '0.25rem'
              }}>
                메인페이지
              </label>
              <input
                type="text"
                value={addForm.main_page_url || ''}
                onChange={(e) => setAddForm({ ...addForm, main_page_url: e.target.value })}
                placeholder="https://www.example.com"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: designSystem.colors.textPrimary,
                marginBottom: '0.25rem'
              }}>
                상품카테고리
              </label>
              <input
                type="text"
                value={addForm.category_page_url || ''}
                onChange={(e) => setAddForm({ ...addForm, category_page_url: e.target.value })}
                placeholder="https://www.example.com/categories"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: designSystem.colors.textPrimary,
                marginBottom: '0.25rem'
              }}>
                상품상세
              </label>
              <input
                type="text"
                value={addForm.product_detail_url || ''}
                onChange={(e) => setAddForm({ ...addForm, product_detail_url: e.target.value })}
                placeholder="https://www.example.com/products/123"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: designSystem.colors.textPrimary,
                marginBottom: '0.25rem'
              }}>
                로그인/회원가입
              </label>
              <input
                type="text"
                value={addForm.login_signup_url || ''}
                onChange={(e) => setAddForm({ ...addForm, login_signup_url: e.target.value })}
                placeholder="https://login.example.com"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: designSystem.colors.textPrimary,
                marginBottom: '0.25rem'
              }}>
                장바구니
              </label>
              <input
                type="text"
                value={addForm.cart_url || ''}
                onChange={(e) => setAddForm({ ...addForm, cart_url: e.target.value })}
                placeholder="https://www.example.com/cart"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${designSystem.colors.border}`,
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
          </div>
          {showOtherPages && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1rem',
              marginBottom: '1rem',
              paddingTop: '1rem',
              borderTop: `1px solid ${designSystem.colors.border}`
            }}>
              {[1, 2, 3, 4, 5].map((num) => (
                <div key={num}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: designSystem.colors.textPrimary,
                    marginBottom: '0.25rem'
                  }}>
                    기타페이지{num}
                  </label>
                  <input
                    type="text"
                    value={(addForm as any)[`other_page_${num}_url`] || ''}
                    onChange={(e) => setAddForm({ ...addForm, [`other_page_${num}_url`]: e.target.value })}
                    placeholder="https://www.example.com/other"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: `1px solid ${designSystem.colors.border}`,
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              onClick={handleAdd}
              style={{
                padding: '0.5rem 1.5rem',
                background: designSystem.colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              추가
            </button>
            <button
              onClick={cancelAdd}
              style={{
                padding: '0.5rem 1.5rem',
                background: designSystem.colors.textMuted,
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* 테이블 */}
      <div style={{
        background: designSystem.colors.bgSecondary,
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem'
          }}>
            <thead>
              <tr style={{ background: designSystem.colors.bgPrimary }}>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  borderBottom: `2px solid ${designSystem.colors.border}`,
                  width: '40px'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === sites.length && sites.length > 0}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  borderBottom: `2px solid ${designSystem.colors.border}`,
                  minWidth: '120px'
                }}>사이트명</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  borderBottom: `2px solid ${designSystem.colors.border}`,
                  minWidth: '200px'
                }}>메인페이지</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  borderBottom: `2px solid ${designSystem.colors.border}`,
                  minWidth: '200px'
                }}>상품카테고리</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  borderBottom: `2px solid ${designSystem.colors.border}`,
                  minWidth: '200px'
                }}>상품상세</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  borderBottom: `2px solid ${designSystem.colors.border}`,
                  minWidth: '200px'
                }}>로그인/회원가입</th>
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  borderBottom: `2px solid ${designSystem.colors.border}`,
                  minWidth: '200px'
                }}>장바구니</th>
                {showOtherPages && (
                  <>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: designSystem.colors.textPrimary,
                      borderBottom: `2px solid ${designSystem.colors.border}`,
                      minWidth: '200px'
                    }}>기타페이지1</th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: designSystem.colors.textPrimary,
                      borderBottom: `2px solid ${designSystem.colors.border}`,
                      minWidth: '200px'
                    }}>기타페이지2</th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: designSystem.colors.textPrimary,
                      borderBottom: `2px solid ${designSystem.colors.border}`,
                      minWidth: '200px'
                    }}>기타페이지3</th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: designSystem.colors.textPrimary,
                      borderBottom: `2px solid ${designSystem.colors.border}`,
                      minWidth: '200px'
                    }}>기타페이지4</th>
                    <th style={{
                      padding: '0.75rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: designSystem.colors.textPrimary,
                      borderBottom: `2px solid ${designSystem.colors.border}`,
                      minWidth: '200px'
                    }}>기타페이지5</th>
                  </>
                )}
                <th style={{
                  padding: '0.75rem',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: designSystem.colors.textPrimary,
                  borderBottom: `2px solid ${designSystem.colors.border}`,
                  width: '150px'
                }}>작업</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={showOtherPages ? 13 : 8} style={{ padding: '2rem', textAlign: 'center' }}>
                    로딩 중...
                  </td>
                </tr>
              ) : sites.length === 0 ? (
                <tr>
                  <td colSpan={showOtherPages ? 13 : 8} style={{ padding: '2rem', textAlign: 'center' }}>
                    등록된 사이트가 없습니다.
                  </td>
                </tr>
              ) : (
                sites.map((site) => {
                  const isEditing = editingId === site.id;

                  return (
                    <tr key={site.id} style={{
                      borderBottom: `1px solid ${designSystem.colors.border}`
                    }}>
                      <td style={{ padding: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(site.id)}
                          onChange={() => toggleSelection(site.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '0.75rem', fontWeight: '600' }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.site_name || ''}
                            onChange={(e) => setEditForm({ ...editForm, site_name: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.25rem',
                              border: `1px solid ${designSystem.colors.border}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem'
                            }}
                          />
                        ) : (
                          site.site_name
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.main_page_url || ''}
                            onChange={(e) => setEditForm({ ...editForm, main_page_url: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.25rem',
                              border: `1px solid ${designSystem.colors.border}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem'
                            }}
                          />
                        ) : (
                          renderUrlLink(site.main_page_url)
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.category_page_url || ''}
                            onChange={(e) => setEditForm({ ...editForm, category_page_url: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.25rem',
                              border: `1px solid ${designSystem.colors.border}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem'
                            }}
                          />
                        ) : (
                          renderUrlLink(site.category_page_url)
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.product_detail_url || ''}
                            onChange={(e) => setEditForm({ ...editForm, product_detail_url: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.25rem',
                              border: `1px solid ${designSystem.colors.border}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem'
                            }}
                          />
                        ) : (
                          renderUrlLink(site.product_detail_url)
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.login_signup_url || ''}
                            onChange={(e) => setEditForm({ ...editForm, login_signup_url: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.25rem',
                              border: `1px solid ${designSystem.colors.border}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem'
                            }}
                          />
                        ) : (
                          renderUrlLink(site.login_signup_url)
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.cart_url || ''}
                            onChange={(e) => setEditForm({ ...editForm, cart_url: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.25rem',
                              border: `1px solid ${designSystem.colors.border}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.875rem'
                            }}
                          />
                        ) : (
                          renderUrlLink(site.cart_url)
                        )}
                      </td>
                      {showOtherPages && (
                        <>
                          <td style={{ padding: '0.75rem' }}>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.other_page_1_url || ''}
                                onChange={(e) => setEditForm({ ...editForm, other_page_1_url: e.target.value })}
                                style={{
                                  width: '100%',
                                  padding: '0.25rem',
                                  border: `1px solid ${designSystem.colors.border}`,
                                  borderRadius: '0.25rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                            ) : (
                              renderUrlLink(site.other_page_1_url)
                            )}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.other_page_2_url || ''}
                                onChange={(e) => setEditForm({ ...editForm, other_page_2_url: e.target.value })}
                                style={{
                                  width: '100%',
                                  padding: '0.25rem',
                                  border: `1px solid ${designSystem.colors.border}`,
                                  borderRadius: '0.25rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                            ) : (
                              renderUrlLink(site.other_page_2_url)
                            )}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.other_page_3_url || ''}
                                onChange={(e) => setEditForm({ ...editForm, other_page_3_url: e.target.value })}
                                style={{
                                  width: '100%',
                                  padding: '0.25rem',
                                  border: `1px solid ${designSystem.colors.border}`,
                                  borderRadius: '0.25rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                            ) : (
                              renderUrlLink(site.other_page_3_url)
                            )}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.other_page_4_url || ''}
                                onChange={(e) => setEditForm({ ...editForm, other_page_4_url: e.target.value })}
                                style={{
                                  width: '100%',
                                  padding: '0.25rem',
                                  border: `1px solid ${designSystem.colors.border}`,
                                  borderRadius: '0.25rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                            ) : (
                              renderUrlLink(site.other_page_4_url)
                            )}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editForm.other_page_5_url || ''}
                                onChange={(e) => setEditForm({ ...editForm, other_page_5_url: e.target.value })}
                                style={{
                                  width: '100%',
                                  padding: '0.25rem',
                                  border: `1px solid ${designSystem.colors.border}`,
                                  borderRadius: '0.25rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                            ) : (
                              renderUrlLink(site.other_page_5_url)
                            )}
                          </td>
                        </>
                      )}
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              onClick={saveEdit}
                              style={{
                                padding: '0.375rem 0.75rem',
                                background: designSystem.colors.success,
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              저장
                            </button>
                            <button
                              onClick={cancelEdit}
                              style={{
                                padding: '0.375rem 0.75rem',
                                background: designSystem.colors.textMuted,
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              취소
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => startEdit(site)}
                              style={{
                                padding: '0.375rem 0.75rem',
                                background: designSystem.colors.primary,
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              수정
                            </button>
                            <button
                              onClick={() => handleDelete(site.id)}
                              style={{
                                padding: '0.375rem 0.75rem',
                                background: designSystem.colors.danger,
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              삭제
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SitePagesManager;
